import { useEffect, useId, useRef, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { Problem } from '../api/types';
import { Button } from '../components/Button';
import { ProblemNotice } from '../components/ProblemNotice';
import { toProblem } from '../app/useAsync';

export interface DeleteDialogProps {
  /** 消す対象の種別。「Workspace」「microapp」。見出しと確認入力の文言に使う。 */
  kind: string;
  /** 確認のために入力させる名前。前後の空白を除いて完全一致したときだけ実行できる。 */
  name: string;
  /** 削除で消えるもの。何を失うのかを実行前に読めるよう、省略せずに並べる。 */
  consequences: string[];
  /** 消えないもの・巻き込まないものの補足。 */
  note?: string;
  /**
   * 削除の実行。成功したら呼び出し側が画面を移り、その結果このダイアログも閉じる。
   * 冪等キーは、このダイアログを開いている間ずっと同じ値を渡す。
   */
  onDelete(idempotencyKey: string): Promise<void>;
  /** 失敗の意味と次の行動。対象ごとに違うcode（409など）の文言は画面側が決める。nullなら既定の文言。 */
  describeFailure?(problem: Problem): string | null;
  onClose(): void;
}

/**
 * 取り消せない削除の確認ダイアログ。
 *
 * 押し間違いで消さないよう、対象の名前を入力して一致したときだけ実行できる。
 * Enterでの暗黙送信（IMEの確定Enterを含む）では実行せず、ボタンの明示的なクリックだけで実行する。
 * ネイティブの<dialog>をshowModal()で開き、背景の操作不可・最前面への配置・Escでの取消はブラウザに任せる。
 */
export function DeleteDialog({
  kind,
  name,
  consequences,
  note,
  onDelete,
  describeFailure,
  onClose,
}: DeleteDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const [typed, setTyped] = useState('');
  const [running, setRunning] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  // 冪等キーは1回の削除操作に1つ。503や通信断の後の再試行も同じキーで送り、
  // 削除が済んでいれば404ではなく最初の応答が返るようにする。閉じて開き直したら別の操作として作り直す。
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  // マウントしたら開く。StrictModeでeffectが2回走っても開き直さないようopenを見る。
  // cleanupではclose()しない。closeイベントが親へ伝わり、開いたばかりのダイアログを閉じてしまうため。
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    dialog.showModal();
    inputRef.current?.focus();
  }, []);

  const target = name.trim();
  const confirmed = target !== '' && typed.trim() === target;

  const submit = async () => {
    if (!confirmed || running) return;
    setRunning(true);
    setProblem(null);
    try {
      await onDelete(idempotencyKey);
      // 成功後は呼び出し側が遷移して閉じる。それまで二重に押せないよう実行中のままにする。
    } catch (error) {
      setProblem(toProblem(error));
      setRunning(false);
    }
  };

  // 閉じる経路はネイティブのcloseに一本化する（キャンセルもEscもここを通る）。
  // close()で閉じると、ブラウザが開く前のフォーカス位置へ戻してくれる。
  const cancel = () => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
    else onClose();
  };

  const hint = problem ? (describeFailure?.(problem) ?? defaultFailureHint(problem)) : null;
  // 409・503・通信断は、待つか直してから再試行すれば解ける。赤ではなく注意色で出す。
  const tone =
    problem && (problem.status === 0 || problem.status === 409 || problem.status === 503)
      ? 'notice'
      : 'error';

  return (
    <dialog
      ref={dialogRef}
      className="dialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onCancel={(event) => {
        // 実行中はEscで閉じさせない。成功の遷移か失敗の理由を見せるまで開いておく。
        if (running) event.preventDefault();
      }}
      onClose={() => {
        // Chromeは続けて押されたEscをcancelで止められずに閉じることがある。実行中なら開き直す。
        if (running) {
          dialogRef.current?.showModal();
          return;
        }
        onClose();
      }}
    >
      <div className="dialog-head">
        <h2 id={titleId} className="dialog-title">
          {kind}「{target}」を削除
        </h2>
      </div>
      <div className="dialog-body">
        <div id={descriptionId} className="dialog-description">
          <p className="dialog-irreversible">この操作は取り消せません。</p>
          <p>次のものがすべて削除されます。</p>
          <ul className="dialog-consequences">
            {consequences.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {note === undefined ? null : <p className="hint">{note}</p>}
        </div>
        <label className="field">
          <span className="field-label">
            確認のため、{kind}名「{target}」を入力してください
          </span>
          <input
            ref={inputRef}
            className="input"
            value={typed}
            disabled={running}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setTyped(event.target.value)}
          />
        </label>
        <ProblemNotice problem={problem} tone={tone} />
        {hint === null ? null : <p className="dialog-hint">{hint}</p>}
      </div>
      <div className="dialog-actions">
        <Button disabled={running} onClick={cancel}>
          キャンセル
        </Button>
        <Button variant="danger" disabled={!confirmed || running} onClick={() => void submit()}>
          {running ? '削除中…' : '削除する'}
        </Button>
      </div>
    </dialog>
  );
}

/** 対象を問わない失敗の読み方。APIのdetailは英語のことがあるので、次の行動を日本語で添える。 */
function defaultFailureHint(problem: Problem): string | null {
  if (problem.status === 0) {
    return '応答を受け取れませんでした。削除が済んだかどうかは分かりません。もう一度削除すると、済んでいれば同じ結果が返り、済んでいなければ続きから削除します。';
  }
  if (problem.status === 503) {
    return '依存先（engineなど）に到達できず、削除を途中で止めました。時間をおいてもう一度削除すると、続きから完了します。';
  }
  if (problem.status === 404) {
    return '対象が見つかりません。別の画面ですでに削除された可能性があります。';
  }
  return null;
}
